import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@mui/material';
import { FC, FormEvent, useEffect, useState } from 'react';
import { useTranslation } from '../../hook/translation.hook';

type OpmPdReasonFormDialogProps = {
  open: boolean;
  title: string;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
};

const OpmPdReasonFormDialog: FC<OpmPdReasonFormDialogProps> = ({
  open,
  title,
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  const [reason, setReason] = useState('');

  const t = useTranslation();
  const isSubmitDisabled = isSubmitting || !reason.trim();

  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitDisabled) return;
    onSubmit(reason);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          {title}
          <IconButton aria-label={t('Common.close')} onClick={handleClose} size="small" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={20} /> : <CloseIcon />}
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            value={reason}
            label={t('opm_pd.reason')}
            onChange={(event) => setReason(event.target.value)}
            variant="outlined"
            fullWidth
            multiline
            minRows={5}
            maxRows={8}
            disabled={isSubmitting}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('opm_pd.cancel')}
          </Button>
          <Button
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            variant="contained"
            color="secondary"
            type="submit"
            disabled={isSubmitDisabled}
          >
            {t('opm_pd.submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default OpmPdReasonFormDialog;
